import type {
  Questionnaire,
  QuestionnaireStatus,
} from '@/_types/models/questionnaire';
import type {
  QuestionnaireCardSection,
  Section,
  SectionState,
} from '@/_types/models/sections';

import { Question } from '@/_types/models/questionnaire';
import {
  getPendingQuestions,
  readAnswerArray,
  getTriggeredQuestionsFromQuestions,
  isQuestionTriggered,
  getSectionQuestions,
  filterClientEntryQuestions,
  getIncompleteQuestions,
} from './questions';
import { TRIGGER_ANSWER_SEPARATOR } from '@/_constants/questions';

const _getIncomplete = (isDirty: boolean = false): SectionState =>
  isDirty ? 'error' : 'incomplete';

export const _getQuestionsCompleted = (
  questions: Array<Question> = [],
): boolean => {
  const areQuestionsCompleted: boolean = questions?.every(
    (q) => (!!q.answer && q.answer !== '0') || q.optional,
  );

  return areQuestionsCompleted;
};

export const getIncompleteSections = (
  sections: Array<Section>,
  shouldSkipSignatureSection?: boolean,
): Array<Section> => {
  if (
    !Array.isArray(sections) ||
    (Array.isArray(sections) && !sections?.length)
  ) {
    return [];
  }

  const allQuestions: Array<Question> = filterClientEntryQuestions(
    sections?.flatMap((s) => s.questionnaire_questions || []),
  );

  const incompleteSections: Array<Section> = sections
    ?.filter((section) => {
      const activeQuestions: Array<Question> =
        getTriggeredQuestionsFromQuestions(
          filterClientEntryQuestions(section?.questionnaire_questions || []),
        );

      const isTriggeredSection = sectionTriggered(
        section,
        sections,
        allQuestions,
      );

      if (!isTriggeredSection) return undefined;

      const isCompleted: boolean = _getQuestionsCompleted(activeQuestions);
      const hasPending: boolean =
        !!getPendingQuestions(activeQuestions)?.length;

      const isQuestionnaireType: boolean =
        section.section_key === 'questionnaire' || section.section_type === 3;

      if (isQuestionnaireType) {
        const isQuestionnaireCompleted: boolean = isCompleted && !hasPending;

        return !isQuestionnaireCompleted ? section : undefined;
      }

      const isEngagementOrCustomCompleted: boolean = shouldSkipSignatureSection
        ? true
        : (section.section_type === 2 || section.section_type === 0) &&
          section.marked_completed;

      const isReviewAndSubmitCompleted: boolean =
        section.section_type === 4 ||
        section.section_key === 'review_and_submit';

      const isDocumentChecklistCompleted: boolean =
        section.section_type === 10 ||
        section.section_key === 'document_checklist';

      const isGenerallyCompleted: boolean =
        isEngagementOrCustomCompleted ||
        isReviewAndSubmitCompleted ||
        isDocumentChecklistCompleted;

      return !isGenerallyCompleted ? section : undefined;
    })
    .filter(Boolean);

  return incompleteSections;
};

export const getSectionStatus = (
  allQuestions: Array<Question>,
  section?: Partial<Section>,
  isDirty?: boolean,
): SectionState => {
  if (!section?.id) return 'incomplete';

  const cleanQuestions: Array<Question> =
    filterClientEntryQuestions(allQuestions);

  switch (section?.section_type) {
    case 0: {
      return section?.marked_completed ? 'completed' : _getIncomplete(isDirty);
    }
    case 10: {
      const uploadQuestions: Array<Question> = cleanQuestions
        ?.filter((q) => q.question_type === 'upload')
        .filter((q) => q.triggered && !q.optional);

      const areUploadCompleted: boolean = uploadQuestions?.every(
        (q) => !!q.answer,
      );

      const isSectionMarkedCompleted: boolean = !!section?.marked_completed;

      return areUploadCompleted || isSectionMarkedCompleted
        ? 'completed'
        : _getIncomplete(isDirty);
    }
    case 2: {
      return section?.marked_completed ? 'completed' : _getIncomplete(isDirty);
    }
    case 4: {
      return section?.marked_completed ? 'completed' : _getIncomplete(isDirty);
    }
    case 3: {
      const activeQuestions: Array<Question> = getSectionQuestions(
        cleanQuestions,
        section,
      );

      const isQuestionnaireCompleted: boolean =
        _getQuestionsCompleted(activeQuestions);
      const isQuestionnairePending: boolean =
        !!getPendingQuestions(activeQuestions)?.length;

      if (isQuestionnairePending) return 'pending';

      return isQuestionnaireCompleted ? 'completed' : _getIncomplete(isDirty);
    }
    default: {
      return 'incomplete';
    }
  }
};

export const getTriggeredSections = (
  allSections: Array<Section> = [],
  allQuestions: Array<Question> = [],
  excludeTriggered?: boolean,
): Array<Section> => {
  if (!allSections?.length) return [];

  return allSections?.filter((section) =>
    sectionTriggered(section, allSections, allQuestions, excludeTriggered),
  );
};

export const sectionTriggered = (
  section: Section,
  allSections: Array<Section>,
  allQuestions: Array<Question>,
  excludeTriggered?: boolean,
  visited: Set<number> = new Set(),
): boolean => {
  if (!section?.id) return false;
  if (excludeTriggered && !section?.triggered) return false;
  if (!section?.trigger_question_id || !section?.trigger_answer) return true;
  if (visited.has(section.id)) return true;

  visited.add(section.id);

  const question = allQuestions?.find(
    (q) => q?.id === section.trigger_question_id,
  );

  const answerArray = readAnswerArray(question?.answer);
  const isTriggerQuestionAnsweredCorrectly =
    answerArray.includes(section.trigger_answer) ||
    section.trigger_answer
      .split(TRIGGER_ANSWER_SEPARATOR)
      .some((a) => answerArray.includes(a));

  if (!isTriggerQuestionAnsweredCorrectly) return false;

  const questionSection = allSections?.find(
    (s) => s.id === question?.section_id,
  );

  if (!questionSection) return false;

  const isTriggerQuestionSectionTriggered = sectionTriggered(
    questionSection,
    allSections,
    allQuestions,
    excludeTriggered,
    visited,
  );

  if (!isTriggerQuestionSectionTriggered) return false;

  return isQuestionTriggered(questionSection, question);
};

export const mapQuestionnaireSection = (
  section: Section,
): QuestionnaireCardSection => {
  return {
    id: section.id,
    name: section.name,
    isCompleted: section.marked_completed,
  };
};

export const mapQuestionnaireStatus = (
  questionnaire: Questionnaire,
): QuestionnaireStatus => {
  if (questionnaire.locked) {
    return 'locked';
  } else if (questionnaire.completed) {
    return 'completed';
  } else if (questionnaire.in_progress) {
    return 'in_progress';
  } else if (questionnaire.opened_timestamp) {
    return 'opened';
  } else if (!questionnaire.opened_timestamp) {
    return 'ready_to_start';
  }

  return 'in_progress';
};

export const getBlockedSections = (
  sections: Array<Section>,
  shouldSkipSignatureSection?: boolean,
): {
  questionnaireBlockedQuestions: Record<number, Array<Question>>;
  firstBlockedQuestion?: Question;
  firstBlockedSectionId?: number;
  hasBlockedSection?: boolean;
  isQuestionnairePending: boolean;
  hasBlockedQuestion?: boolean;
} => {
  const incompleteSections: Array<Section> = getIncompleteSections(
    sections,
    shouldSkipSignatureSection,
  );
  const incompleteSectionIds: Array<number> = Array.from(
    new Set(incompleteSections?.map((section) => section.id)),
  );

  if (!incompleteSectionIds?.length) {
    return { isQuestionnairePending: false, questionnaireBlockedQuestions: {} };
  }

  const questionnaireBlockedRecord: Record<number, Array<Question>> = {};

  for (const sectionId of incompleteSectionIds) {
    const questions: Array<Question> =
      incompleteSections.find((s) => s.id === sectionId)
        ?.questionnaire_questions || [];

    const incompleteQuestions: Array<Question> =
      getIncompleteQuestions(questions);

    questionnaireBlockedRecord[sectionId as number] = incompleteQuestions;
  }

  const firstBlockedSectionId: number | undefined =
    Object.keys(questionnaireBlockedRecord).length > 0
      ? Number(Object.keys(questionnaireBlockedRecord)?.[0])
      : undefined;

  const firstBlockedQuestion: Question | undefined = firstBlockedSectionId
    ? questionnaireBlockedRecord[firstBlockedSectionId]?.[0]
    : undefined;

  const hasBlockedSection: boolean =
    !!firstBlockedSectionId && !firstBlockedQuestion?.id;

  const hasBlockedQuestion: boolean = !!firstBlockedQuestion?.id;
  const isQuestionnairePending: boolean =
    hasBlockedSection || hasBlockedQuestion;

  return {
    isQuestionnairePending,
    questionnaireBlockedQuestions: questionnaireBlockedRecord,
    hasBlockedSection,
    hasBlockedQuestion,
    firstBlockedSectionId,
    firstBlockedQuestion,
  };
};

export const filterUploadOnlySections = (
  sections: Array<Section>,
  areUploadsHidden: boolean,
): Array<Section> => {
  if (!areUploadsHidden || sections.length <= 1) return sections;

  return sections.filter(
    (section) =>
      section.section_type !== 3 ||
      section.questionnaire_questions?.some(
        (q) => q.question_type !== 'upload',
      ),
  );
};
